import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Empty, PageHeader, Pager, Td, Th } from "../components/common/UI";
import { PriorityBadge } from "../components/common/Badges";

export default function Tasks() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const scope = params.get("scope") || "mine";
  const status = params.get("status") || "open,in_progress";
  const load = useCallback(() => {
    api.get("/tasks", { params: { scope, status, page: params.get("page") || 1 } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [scope, status, params]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (t, s) => {
    try {
      await api.put(`/tasks/${t.id}`, { status: s });
      toast.success("Task updated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const setP = (k, v) => { const n = new URLSearchParams(params); n.set(k, v); n.delete("page"); setParams(n); };
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Work queue" title="My Tasks" subtitle="Tasks are generated automatically by workflow transitions and deadline rules, or assigned manually." />
      <div className="mb-4 flex flex-wrap gap-2">
        <select className="otms-input w-44" value={scope} onChange={(e) => setP("scope", e.target.value)} data-testid="task-scope-select">
          <option value="mine">Assigned to me / my roles</option>
          {can("measure.assign") && <option value="all">All LGU tasks</option>}
        </select>
        <select className="otms-input w-44" value={status} onChange={(e) => setP("status", e.target.value)} data-testid="task-status-filter">
          <option value="open,in_progress">Open</option><option value="overdue">Overdue</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
        </select>
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[820px]" data-testid="tasks-table">
          <thead><tr><Th>Task</Th><Th>Measure</Th><Th>Priority</Th><Th>Due</Th><Th>Status</Th></tr></thead>
          <tbody>
            {data?.items.map((t) => (
              <tr key={t.id} data-testid={`task-row-${t.id}`}>
                <Td><div className="font-medium text-slate-800">{t.title}</div>{t.description && <div className="text-[11px] text-slate-500">{t.description}</div>}</Td>
                <Td>{t.measure_id ? <Link className="text-sky-700 hover:underline" to={`/measures/${t.measure_id}`}>{t.measure_ref}</Link> : "—"}</Td>
                <Td><PriorityBadge value={t.priority} /></Td>
                <Td className={t.is_overdue ? "font-semibold text-red-600" : ""}>{fmtDateTime(t.due_date)}{t.is_overdue && <div className="text-[10px] uppercase">Overdue</div>}</Td>
                <Td>
                  <select className="otms-input h-8 w-36 text-xs" value={t.status} onChange={(e) => setStatus(t, e.target.value)} data-testid={`task-status-select-${t.id}`}>
                    <option value="open">Open</option><option value="in_progress">In progress</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
                  </select>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.items.length === 0 && <Empty text="No tasks in this view." />}
      </div>
      <Pager meta={data} onPage={(p) => setP("page", p)} />
    </div>
  );
}
